import { ArrayQueue } from "@service/array-queue";
import sleep from "@utils/sleep";

class AudioQueue {
  private readonly samples: ArrayQueue<Uint8Array> = new ArrayQueue(10);
  private processing = false;
  private mutex = Promise.resolve();

  add(array: Uint8Array) {
    this.samples.push(array);
  }

  async process(
    callback: (blob: Blob | ArrayBufferLike) => void,
  ): Promise<void> {
    await this.mutex;
    this.mutex = (async () => {
      if (this.processing) {
        return;
      }
      this.processing = true;
      try {
        while (this.processing) {
          if (this.samples.length()) {
            callback(this.samples.shift()!);
          } else {
            await sleep(10);
          }
        }
      } finally {
        this.processing = false;
      }
    })();
  }

  stop(): void {
    this.processing = false;
  }
}

export class MicStream {
  private readonly processor: ScriptProcessorNode;
  private readonly audioContext: AudioContext;
  private readonly input: MediaStreamAudioSourceNode;
  private readonly outputSampleRate = 44100;
  private readonly bufferSize = 2048;
  private readonly queue = new AudioQueue();

  constructor(
    stream: MediaStream,
    private readonly onVoiceRecorded: (blob: Blob | ArrayBufferLike) => void,
  ) {
    this.audioContext = new AudioContext();
    this.input = this.audioContext.createMediaStreamSource(stream);
    this.processor = this.audioContext.createScriptProcessor(
      this.bufferSize,
      1,
      1,
    );
    this.input.connect(this.processor);

    this.processor.connect(this.audioContext.destination);

    this.processor.onaudioprocess = async (e) => {
      const downSampledBuffer = downSampleBuffer(
        e.inputBuffer.getChannelData(0),
        this.audioContext.sampleRate,
        this.outputSampleRate,
      );
      const pcmEncodedBuffer = pcmEncode(downSampledBuffer);

      this.queue.add(new Uint8Array(pcmEncodedBuffer));
    };
  }

  start(): void {
    this.queue.process(this.onVoiceRecorded);
  }

  stop(): void {
    this.queue.stop();
  }

  async destroy() {
    try {
      this.processor.disconnect(this.audioContext.destination);
      this.input.disconnect(this.processor);
      await this.audioContext.close();
    } catch (error) {
      console.error(error);
    }
  }
}

export function pcmEncode(input: Float32Array): ArrayBuffer {
  let offset = 0;
  const buffer = new ArrayBuffer(input.length * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < input.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, input[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
}

export function downSampleBuffer(
  buffer: Float32Array,
  inputSampleRate = 44100,
  outputSampleRate = 16000,
): Float32Array {
  if (outputSampleRate === inputSampleRate) {
    return buffer;
  }

  const sampleRateRatio = inputSampleRate / outputSampleRate;
  const newLength = Math.round(buffer.length / sampleRateRatio);
  const result = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;

  while (offsetResult < result.length) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);

    let accum = 0;
    let count = 0;

    for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
      accum += buffer[i];
      count++;
    }
    result[offsetResult] = accum / count;
    offsetResult++;
    offsetBuffer = nextOffsetBuffer;
  }
  return result;
}
